import { Auth, Implementation } from '@leagueplatform/auth';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { getPartnerAccessToken } from '@leagueplatform/sso-util';
import { log } from '@telus-alberta-health-web/shared-module/src/utilities/log';

export class IdentityToken {
  private token: string | null = null;

  private tokenExpiresAtMs: number | null = null;

  private readonly refreshSkewMs = 30 * 1000;

  private partnerId: string;

  constructor(partnerId: string) {
    this.token = null;
    this.partnerId = partnerId;
  }

  // Extracts JWT `exp` (seconds) and converts it to epoch milliseconds.
  private static getTokenExpFromJwt(token: string): number | null {
    try {
      // Parse JWT payload locally so we can proactively refresh before expiry.
      const [, payload] = token.split('.');
      if (!payload) return null;
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(base64);
      const parsed = JSON.parse(decoded) as { exp?: number };
      return parsed.exp ? parsed.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  // Determines whether cached token is still usable with a small safety buffer.
  private isTokenFresh(): boolean {
    return Boolean(
      this.token &&
        this.tokenExpiresAtMs &&
        // Apply a small skew to avoid racing token expiry in-flight.
        Date.now() < this.tokenExpiresAtMs - this.refreshSkewMs,
    );
  }

  // Requests a new partner token and updates local cache/expiry metadata.
  async fetchToken() {
    try {
      const newToken = await getPartnerAccessToken(this.partnerId);
      if (newToken) {
        this.token = newToken;
        this.tokenExpiresAtMs = IdentityToken.getTokenExpFromJwt(newToken);
      }
    } catch (err: any) {
      const isAuthenticated =
        Auth.implementation === Implementation.EMBEDDED
          ? true
          : await StandaloneAuth.client.isAuthenticated();

      // only throw the error if user is authenticated
      if (isAuthenticated) {
        throw new Error(err);
      } else {
        log('Error fetching class identity token while not authenticated');
      }
    }
  }

  // Explicit refresh entrypoint used by live-chat RefreshToken event handling.
  async refreshToken() {
    await this.fetchToken();
  }

  // Returns cached token when fresh, otherwise fetches a replacement token.
  async getToken() {
    if (this.isTokenFresh()) {
      return this.token;
    }
    // Fetch only when token is missing/stale; callers can safely call per request.
    await this.fetchToken();
    return this.token;
  }
}
